import React, { createContext, useContext, useState, useCallback } from 'react';
import type { ReactNode } from 'react';
import type { ShortcutDefinition } from '../config/shortcuts';

export interface KeyboardShortcutsContextType {
  /** Whether shortcuts are globally enabled */
  enabled: boolean;
  /** Toggle shortcuts on/off */
  toggleShortcuts: () => void;
  /** Enable shortcuts */
  enableShortcuts: () => void;
  /** Disable shortcuts */
  disableShortcuts: () => void;
  /** Register a shortcut for help modal */
  registerShortcut: (shortcut: ShortcutDefinition) => void;
  /** Unregister a shortcut */
  unregisterShortcut: (keys: string) => void;
  /** Get all registered shortcuts */
  getRegisteredShortcuts: () => ShortcutDefinition[];
  /** Show help modal */
  showHelpModal: () => void;
  /** Hide help modal */
  hideHelpModal: () => void;
  /** Whether help modal is visible */
  isHelpModalVisible: boolean;
  /** Currently pressed keys in sequence */
  sequenceKeys: string[];
  /** Add a key to the sequence */
  addSequenceKey: (key: string) => void;
  /** Clear the sequence */
  clearSequence: () => void;
  /** Mark sequence as successful */
  markSequenceSuccess: (destination: string) => void;
  /** Mark sequence as failed */
  markSequenceFailed: () => void;
  /** Sequence status */
  sequenceStatus: 'idle' | 'success' | 'failed';
  /** Success destination message */
  successDestination: string;
}

const KeyboardShortcutsContext = createContext<KeyboardShortcutsContextType | undefined>(undefined);

export interface KeyboardShortcutsProviderProps {
  children: ReactNode;
  /** Initial enabled state */
  initialEnabled?: boolean;
}

export const KeyboardShortcutsProvider: React.FC<KeyboardShortcutsProviderProps> = ({
  children,
  initialEnabled = true
}) => {
  const [enabled, setEnabled] = useState(initialEnabled);
  const [registeredShortcuts, setRegisteredShortcuts] = useState<ShortcutDefinition[]>([]);
  const [isHelpModalVisible, setIsHelpModalVisible] = useState(false);
  const [sequenceKeys, setSequenceKeys] = useState<string[]>([]);
  const [sequenceTimeout, setSequenceTimeout] = useState<number | null>(null);
  const [awaitingNextKey, setAwaitingNextKey] = useState(false);
  const [sequenceStatus, setSequenceStatus] = useState<'idle' | 'success' | 'failed'>('idle');
  const [successDestination, setSuccessDestination] = useState('');

  const toggleShortcuts = useCallback(() => {
    setEnabled(prev => !prev);
  }, []);

  const enableShortcuts = useCallback(() => {
    setEnabled(true);
  }, []);

  const disableShortcuts = useCallback(() => {
    setEnabled(false);
  }, []);

  const registerShortcut = useCallback((shortcut: ShortcutDefinition) => {
    setRegisteredShortcuts(prev => {
      const existing = prev.find(s => s.keys === shortcut.keys);
      if (existing) {
        return prev.map(s => s.keys === shortcut.keys ? shortcut : s);
      }
      return [...prev, shortcut];
    });
  }, []);

  const unregisterShortcut = useCallback((keys: string) => {
    setRegisteredShortcuts(prev => prev.filter(s => s.keys !== keys));
  }, []);

  const getRegisteredShortcuts = useCallback(() => {
    return [...registeredShortcuts];
  }, [registeredShortcuts]);

  const showHelpModal = useCallback(() => {
    setIsHelpModalVisible(true);
  }, []);

  const hideHelpModal = useCallback(() => {
    setIsHelpModalVisible(false);
  }, []);

  const clearSequence = useCallback(() => {
    setSequenceKeys([]);
    setAwaitingNextKey(false);
    setSequenceStatus('idle');
    setSuccessDestination('');
    if (sequenceTimeout) {
      clearTimeout(sequenceTimeout);
      setSequenceTimeout(null);
    }
  }, [sequenceTimeout]);

  const markSequenceSuccess = useCallback((destination: string) => {
    setSequenceStatus('success');
    setSuccessDestination(destination);
    setAwaitingNextKey(false);
    
    // Clear after showing success message
    const timeout = window.setTimeout(() => {
      clearSequence();
    }, 1500);
    
    if (sequenceTimeout) {
      clearTimeout(sequenceTimeout);
    }
    setSequenceTimeout(timeout);
  }, [sequenceTimeout, clearSequence]);

  const markSequenceFailed = useCallback(() => {
    setSequenceStatus('failed');
    setAwaitingNextKey(false);
    
    // Clear after showing failed animation
    const timeout = window.setTimeout(() => {
      clearSequence();
    }, 1000);
    
    if (sequenceTimeout) {
      clearTimeout(sequenceTimeout);
    }
    setSequenceTimeout(timeout);
  }, [sequenceTimeout, clearSequence]);

  const addSequenceKey = useCallback((key: string, isInitialKey: boolean = false) => {
    // Clear any existing timeout
    if (sequenceTimeout) {
      clearTimeout(sequenceTimeout);
    }

    // Add key to sequence
    setSequenceKeys(prev => [...prev, key]);
    
    // If this is the initial key of a sequence, we're now awaiting the next key
    if (isInitialKey) {
      setAwaitingNextKey(true);
    }

    // Set timeout to clear sequence after 2 seconds
    const timeout = window.setTimeout(() => {
      clearSequence();
    }, 2000);
    
    setSequenceTimeout(timeout);
  }, [sequenceTimeout, clearSequence]);

  // Global keyboard listener to detect sequence starts
  React.useEffect(() => {
    if (!enabled) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      // Check if user is typing in an input field
      const activeElement = document.activeElement;
      const tagName = activeElement?.tagName.toLowerCase();
      const isInput = ['input', 'textarea', 'select'].includes(tagName || '');
      const isContentEditable = activeElement?.getAttribute('contenteditable') === 'true';
      
      if (isInput || isContentEditable) {
        return;
      }

      // Detect potential sequence starters (Ctrl+key combinations)
      if ((event.ctrlKey || event.metaKey) && !event.shiftKey && !event.altKey) {
        const key = event.key.toLowerCase();
        
        // Check if this is a known sequence starter (ctrl+d, ctrl+g, ctrl+b)
        if (['d', 'g', 'b'].includes(key)) {
          const combo = `Ctrl+${key.toUpperCase()}`;
          addSequenceKey(combo, true);
        }
      } 
      // If we're awaiting the next key in a sequence
      else if (awaitingNextKey && !event.ctrlKey && !event.metaKey && !event.altKey && !event.shiftKey) {
        const key = event.key.toUpperCase();
        if (key.length === 1) { // Single character keys only
          addSequenceKey(key, false);
          
          // Set a timeout to check if navigation happened
          // If no navigation happens in 300ms, mark as failed
          setTimeout(() => {
            // Check if we're still in awaiting state (no navigation happened)
            if (awaitingNextKey) {
              markSequenceFailed();
            }
          }, 300);
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [enabled, awaitingNextKey, addSequenceKey, clearSequence]);

  const value: KeyboardShortcutsContextType = {
    enabled,
    toggleShortcuts,
    enableShortcuts,
    disableShortcuts,
    registerShortcut,
    unregisterShortcut,
    getRegisteredShortcuts,
    showHelpModal,
    hideHelpModal,
    isHelpModalVisible,
    sequenceKeys,
    addSequenceKey,
    clearSequence,
    markSequenceSuccess,
    markSequenceFailed,
    sequenceStatus,
    successDestination
  };

  return (
    <KeyboardShortcutsContext.Provider value={value}>
      {children}
    </KeyboardShortcutsContext.Provider>
  );
};

/**
 * Hook to use keyboard shortcuts context
 */
export const useKeyboardShortcutsContext = (): KeyboardShortcutsContextType => {
  const context = useContext(KeyboardShortcutsContext);
  
  if (context === undefined) {
    throw new Error('useKeyboardShortcutsContext must be used within a KeyboardShortcutsProvider');
  }
  
  return context;
};

/**
 * Hook to register shortcuts for help modal
 */
export const useRegisterShortcut = (shortcut: ShortcutDefinition) => {
  const { registerShortcut, unregisterShortcut } = useKeyboardShortcutsContext();
  
  React.useEffect(() => {
    registerShortcut(shortcut);
    
    return () => {
      unregisterShortcut(shortcut.keys);
    };
  }, [shortcut, registerShortcut, unregisterShortcut]);
};
